function normalizeString(value) {
  if (typeof value !== "string") {
    return "";
  }

  const trimmed = value.trim();
  return trimmed || "";
}

function normalizeStringList(values) {
  if (!Array.isArray(values)) {
    return [];
  }

  const seen = new Set();
  const normalized = [];

  values.forEach((value) => {
    const nextValue = normalizeString(value);
    if (!nextValue || seen.has(nextValue)) {
      return;
    }

    seen.add(nextValue);
    normalized.push(nextValue);
  });

  return normalized;
}

function createEmptyFacets() {
  return {
    themes: [],
    feelings: [],
    colors: [],
    mediums: [],
    price_ranges: [],
    size_ranges: [],
  };
}

function toFiniteNumber(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function rangesOverlap(left, right) {
  return left.min <= right.max && right.min <= left.max;
}

const PRICE_BANDS = Object.freeze([
  { label: "Under $1000", min: 0, max: 999 },
  { label: "$1,000 - $5,000", min: 1000, max: 5000 },
  { label: "$5,001 - $10,000", min: 5001, max: 10000 },
  { label: "Over $10,000", min: 10001, max: Number.MAX_SAFE_INTEGER },
]);

function resolvePriceRangesFromQuery(price = null) {
  if (!price || typeof price !== "object") {
    return [];
  }

  const displayText = normalizeString(price.displayText);
  if (displayText && price.isCustomPrice !== true) {
    return normalizeStringList([displayText]);
  }

  const minPrice = toFiniteNumber(price.minPrice);
  const maxPrice = toFiniteNumber(price.maxPrice);
  if (minPrice === null || maxPrice === null) {
    return [];
  }

  const candidateRange = {
    min: Math.min(minPrice, maxPrice),
    max: Math.max(minPrice, maxPrice),
  };

  return PRICE_BANDS
    .filter((band) => rangesOverlap(band, candidateRange))
    .map((band) => band.label);
}

function toCentimeters(value, unit) {
  const numeric = toFiniteNumber(value);
  if (numeric === null) {
    return null;
  }

  const normalizedUnit = normalizeString(unit).toLowerCase();
  return normalizedUnit === "inch" || normalizedUnit === "inches" || normalizedUnit === "in"
    ? numeric * 2.54
    : numeric;
}

function resolveSizeBandFromLongestSide(longestSideCm) {
  if (!Number.isFinite(longestSideCm) || longestSideCm < 0) {
    return "";
  }
  if (longestSideCm <= 47) return "Small";
  if (longestSideCm <= 90) return "Medium";
  if (longestSideCm <= 180) return "Large";
  return "Huge";
}

function resolveSizeRangesFromQuery(size = null) {
  if (!size || typeof size !== "object") {
    return [];
  }

  const displayText = normalizeString(size.displayText);
  if (displayText && size.isCustomSize !== true) {
    return normalizeStringList([displayText]);
  }

  const widthCm = toCentimeters(size.width, size.unit);
  const heightCm = toCentimeters(size.height, size.unit);
  if (widthCm === null || heightCm === null) {
    return [];
  }

  const longestSideCm = Math.max(widthCm, heightCm);
  const condition = normalizeString(size.condition).toLowerCase();

  if (condition === "exact") {
    return normalizeStringList([resolveSizeBandFromLongestSide(longestSideCm)]);
  }

  if (condition === "bigger") {
    if (longestSideCm <= 47) return ["Small", "Medium", "Large", "Huge"];
    if (longestSideCm <= 90) return ["Medium", "Large", "Huge"];
    if (longestSideCm <= 180) return ["Large", "Huge"];
    return ["Huge"];
  }

  if (longestSideCm <= 47) return ["Small"];
  if (longestSideCm <= 90) return ["Small", "Medium"];
  if (longestSideCm <= 180) return ["Small", "Medium", "Large"];
  return ["Small", "Medium", "Large", "Huge"];
}

export function buildFacetPayloadFromCategory({ collectionType, category } = {}) {
  const normalizedType = normalizeString(collectionType).toLowerCase();
  const value = normalizeString(category?.name || category?.subcat || category?.label);
  if (!value) {
    return null;
  }

  const facets = createEmptyFacets();

  if (normalizedType === "theme") {
    facets.themes = [value];
  } else if (normalizedType === "emotion" || normalizedType === "feeling") {
    facets.feelings = [value];
  } else if (normalizedType === "color") {
    facets.colors = [value];
  } else if (normalizedType === "medium") {
    facets.mediums = [value];
  } else if (normalizedType === "price") {
    facets.price_ranges = [value];
  } else if (normalizedType === "size") {
    facets.size_ranges = [value];
  } else {
    return null;
  }

  return facets;
}

export function buildFacetPayloadFromSearchQuery(query = {}) {
  return {
    themes: normalizeStringList(query?.theme),
    feelings: normalizeStringList(query?.emotion),
    colors: normalizeStringList(query?.color),
    mediums: normalizeStringList(query?.medium),
    price_ranges: resolvePriceRangesFromQuery(query?.price),
    size_ranges: resolveSizeRangesFromQuery(query?.size),
  };
}

export function diffFacetPayload(nextPayload = null, previousPayload = null) {
  const next = nextPayload && typeof nextPayload === "object" ? nextPayload : createEmptyFacets();
  const previous = previousPayload && typeof previousPayload === "object" ? previousPayload : createEmptyFacets();
  const diff = createEmptyFacets();

  Object.keys(diff).forEach((key) => {
    const previousSet = new Set(normalizeStringList(previous[key]));
    diff[key] = normalizeStringList(next[key]).filter((value) => !previousSet.has(value));
  });

  return diff;
}

export function hasFacetPayloadValues(payload = null) {
  if (!payload || typeof payload !== "object") {
    return false;
  }

  return Object.values(payload).some((value) => Array.isArray(value) && value.length > 0);
}
